"use client";
import Link from "next/link";
import { useState, useMemo, memo, useCallback, startTransition } from "react";
import { useCreateModal } from "@/app/lib/features/modal/modal.hook";
import { cn } from "@/app/utils/cn";
import { Product } from "@/app/lib/features/product/product.types";
import dynamic from "next/dynamic";
import ProductCardImage from "./ProductCardImage";
import ProductCardBadges from "./ProductCardBadges";
import ProductCardBanners from "./ProductCardBanners";
import ProductCardInfo from "./ProductCardInfo";
import ProductCardPrice from "./ProductCardPrice";
import ProductCardActions from "./ProductCardActions";
import ProductCardVariantsSheet from "./ProductCardVariantsSheet";
import ProductCardMobileCartSheet from "./ProductCardMobileCartSheet";
import {
  buildCampaignItems,
  buildSocialProofItems,
  normalizeSizes,
  normalizeVariants,
} from "@/app/utils/productCard.utils";
import { trackProductClick } from "@/app/lib/tracking/interactions";

// Hover/tıklama-only component'ler — initial bundle'dan çıkar, ilk etkileşimde yükle
// NOT: VariantsSheet ve MobileCartSheet isOpen=false ile önceden mount edilmeli
// (dynamic lazy-load + isOpen=true ile mount animasyonu bozar)
const ProductCardHoverActions = dynamic(() => import("./ProductCardHoverActions"), { ssr: false });
const ProductCardVariants = dynamic(() => import("./ProductCardVariants"), { ssr: false });

interface ProductCardProps {
  product: Product;
  variant?: "default" | "favorites" | "outfit";
  size?: "default" | "sm";
  isSelected?: boolean;
  onSelectionChange?: (productId: number, checked: boolean) => void;
  onVariantChange?: (productId: number, variantId: number) => void;
  /** Modal içinden kullanılıyorsa sayfaya gidilmeden önce çağrılır (modal kapatma vb.) */
  onNavigate?: () => void;
  /** Modal içinde kullanılıyorsa AI dene butonunu gizler */
  hideAiButton?: boolean;
  /** LCP optimizasyonu: ilk görünür kart için fetchpriority=high uygular */
  priority?: boolean;
  /**
   * Kart yatay bir carousel içinde (ProductSwiper, TozyMessageBubble) ise geçilir.
   * Her iki değerde de yatay sürükleme carousel'e aittir (swiper-no-swiping
   * uygulanmaz, touch galerisi kapalı) — fark sadece kaç görsel render edildiği:
   *
   * - `"gallery"` → desktop: tüm görseller render edilir, hover ile resim
   *   değiştirme çalışır. Hover ≠ drag olduğu için carousel'le çakışmaz.
   * - `"single"`  → mobil/dokunmatik: sadece images[0]. Touch'ta hover yoktur,
   *   galeriyi ancak swipe ile gezebilirdik; o da carousel'i bloklardı.
   *
   * Değeri carousel `useHoverGallery()` ile belirler. Grid sayfalarında
   * (kategori, arama, favoriler) geçilmez — orada yatay carousel olmadığı için
   * çoklu görsel galerisi swipe dahil aynen çalışır.
   */
  carousel?: "single" | "gallery";
}

const ProductCard = memo<ProductCardProps>(
  ({
    product,
    variant = "default",
    size = "default",
    isSelected = false,
    onSelectionChange,
    onVariantChange,
    onNavigate,
    hideAiButton = false,
    priority = false,
    carousel,
  }) => {
    // Dispatch-only: kart modal state'ini OKUMUYOR. useModal() olsaydı her modal
    // açılış/kapanışında 84 kartın tamamı yeniden render olurdu.
    const createModal = useCreateModal();
    const [isHovered, setIsHovered] = useState(false);
    const [isVariantsOpen, setIsVariantsOpen] = useState(false);
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [isMobileCartOpen, setIsMobileCartOpen] = useState(false);

    const handleOpenCartPicker = useCallback(() => {
      if (window.innerWidth >= 1024) {
        createModal("product-card-cart", { product });
      } else {
        setIsMobileCartOpen(true);
      }
    }, [createModal, product]);
    const [hoveredVariantImage, setHoveredVariantImage] = useState<
      string | null
    >(null);

    // outfit: selected variantId as state (safe to read during render)
    const [outfitVariantId, setOutfitVariantId] = useState<number>(
      product.variants_by_color?.[0]?.sizes?.[0]?.variant_id ??
        product.variants_by_color?.[0]?.variant_id ??
        product.variant_id,
    );

    const isOutfit = variant === "outfit";

    const allSizes = useMemo(() => normalizeSizes(product), [product]);
    const allVariants = useMemo(() => normalizeVariants(product), [product]);
    const socialProofItems = useMemo(
      () => buildSocialProofItems(product),
      [product],
    );
    const campaignItems = useMemo(() => buildCampaignItems(product), [product]);

    // Dokunmatik cihazda carousel içindeyken tek görsel — bkz. `carousel` prop'u.
    const cardImages = useMemo(() => {
      const source = hoveredVariantImage ? [hoveredVariantImage] : product.images;
      return carousel === "single" && source ? source.slice(0, 1) : source;
    }, [hoveredVariantImage, product.images, carousel]);

    // Favori renk varyantı bazında tutuluyor: kart hangi rengi gösteriyorsa
    // kalp onun durumunu gösterir. Kartın kendi varyantı product.variant_id;
    // outfit modunda kullanıcı kart içinden renk değiştirebildiği için seçilen
    // beden varyantının ait olduğu renk aranır.
    const favoriteVariantId = useMemo(() => {
      if (!isOutfit) return product.variant_id;
      const color = product.variants_by_color?.find(
        (v) =>
          v.variant_id === outfitVariantId ||
          v.sizes?.some((s) => s.variant_id === outfitVariantId),
      );
      return color?.variant_id ?? product.variant_id;
    }, [isOutfit, outfitVariantId, product.variants_by_color, product.variant_id]);

    // Ürün seviyesindeki is_favorited yerine o rengin değeri.
    const favoriteInitial =
      product.variants_by_color?.find((v) => v.variant_id === favoriteVariantId)
        ?.is_favorited ?? product.is_favorited;

    const hasVariants = allVariants.length > 1;
    const productUrl = `/${product.slug}`;
    const imageAspectClass = size === "sm" ? "aspect-[3/4]" : "aspect-[2/3]";
    // İlk geçerli beden variant_id'si — cart için doğru ID
    const firstSizeVariantId =
      product.variants_by_color?.[0]?.sizes?.[0]?.variant_id ??
      product.variants_by_color?.[0]?.variant_id ??
      product.variant_id;

    
    return (
      <article
        className="relative w-full h-full overflow-hidden group/card"
        onMouseEnter={() => startTransition(() => setIsHovered(true))}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsVariantsOpen(false);
        }}
      >
        <Link
          href={productUrl}
          // Kaydırma sonrası gelen hayalet click'i bu bileşen DEĞİL,
          // ProductCardImage kendi içinde (onClickCapture) yutuyor. Bayrak jesti
          // başlatan elemanın kendisinde durduğu için "kaydır → tıkla → açılmaz"
          // durumu oluşamaz; buraya sadece yutulmayan gerçek tıklamalar gelir.
          onClick={() => {
            trackProductClick(product.id);
            if (onNavigate) startTransition(() => onNavigate());
          }}
          className="flex flex-col h-full bg-white hover:bg-gray-50 border border-gray-200 hover:border-main hover:shadow-md transition-[border-color,box-shadow,background-color] duration-200 ease-out rounded-lg"
        >
          {/* Image wrapper — relative container HoverActions için overflow dışında */}
          <div className={cn("relative w-full shrink-0", imageAspectClass)}>
            {/* Overflow-hidden sadece image+banners'ı sarar */}
            <div className="absolute inset-0 rounded-t-lg overflow-hidden">
              <ProductCardBanners banners={product.product_banners || []} />
              <ProductCardImage
                images={cardImages}
                name={product.internet_product_description}
                isVideo={product.is_video}
                videoLink={product.video_link}
                priority={priority}
                inCarousel={carousel !== undefined}
              />
            </div>

            {/* HoverActions — overflow-hidden dışında, tam genişlikte. AnimatePresence yerine CSS fade-in */}
            {isHovered && !isVariantsOpen && (
              <div className="hidden md:block absolute inset-0 pointer-events-none animate-card-fade-in">
                <ProductCardHoverActions
                    productId={product.id}
                    productName={product.name}
                    brand={product.brand_name}
                    price={parseFloat(
                      product.price.replace(/\./g, "").replace(",", "."),
                    )}
                    sizes={isOutfit ? [] : allSizes}
                    aiButton={product.try_status && product.ai_button?.button_text ? product.ai_button : undefined}
                    productImage={hoveredVariantImage ?? product.images?.[0]}
                    variantId={firstSizeVariantId}
                    outfitVariantId={isOutfit ? outfitVariantId : undefined}
                    hideAiButton={hideAiButton}
                    onOpenCartSheet={handleOpenCartPicker}
                  />
              </div>
            )}

            {/* Renk paleti butonu */}
            {hasVariants && !isOutfit && (
              <div
                className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 z-20"
                onClick={(e) => e.preventDefault()}
              >
                <button
                  className="flex items-center cursor-pointer gap-1 bg-white rounded-full shadow-md border border-gray-200 px-1.5 py-1 hover:shadow-lg transition-shadow"
                  onMouseEnter={() => setIsVariantsOpen(true)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (window.innerWidth < 768) {
                      setIsSheetOpen(true);
                    }
                  }}
                >
                  <div className="flex -space-x-1">
                    <div className="w-3.5 h-3.5 rounded-full bg-linear-to-br from-blue-400 via-purple-400 to-pink-400 ring-1 ring-white" />
                    <div className="w-3.5 h-3.5 rounded-full bg-linear-to-br from-green-400 via-yellow-400 to-red-400 ring-1 ring-white" />
                  </div>
                  <span className="text-[10px] md:text-[12px] font-semibold text-gray-600 leading-none">
                    {allVariants.length}
                  </span>
                </button>
              </div>
            )}

            {product.sponsored_text && (
              <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 z-10 pointer-events-none">
                <div className="bg-gray-600/90 text-white text-[10px] sm:text-xs px-2 py-0.5 rounded">
                  {product.sponsored_text}
                </div>
              </div>
            )}
          </div>

          <ProductCardBadges badges={product.badges || []} />
          <div className="p-1 sm:p-2 flex flex-col flex-1">
            <ProductCardInfo
              brandName={product.brand_name}
              productName={product.internet_product_description || ""}
              productDescription={product.internet_product_description || ""}
              ratingAverage={product.rating?.average || 0}
              ratingCount={product.rating?.count || 0}
              socialProofItems={socialProofItems}
              campaignItems={campaignItems}
              isOutfit={isOutfit}
              outfitProduct={isOutfit ? product : undefined}
              onOutfitVariantChange={(variantId) => {
                setOutfitVariantId(variantId);
                onVariantChange?.(product.id, variantId);
              }}
              onOutfitColorImageChange={(image) =>
                setHoveredVariantImage(image)
              }
            />
            <ProductCardPrice
              price={product.price}
              listPrice={product.list_price}
              hasDiscount={product.has_discount}
              hasCartSpecialPrice={product.has_cart_special_price}
              cartSpecialPriceText={product.cart_special_price_text}
              currencySymbol={product.currency_symbol}
            />
          </div>
        </Link>

        <ProductCardActions
          productId={product.id}
          productSlug={product.slug}
          variant={variant}
          isFavorited={favoriteInitial}
          favoriteVariantId={favoriteVariantId}
          isSelected={isSelected}
          onSelectionChange={onSelectionChange}
          onOpenMobileCart={
            !isOutfit ? handleOpenCartPicker : undefined
          }
          aiButton={!hideAiButton && product.try_status && product.ai_button?.button_text ? product.ai_button : undefined}
          productImage={hoveredVariantImage ?? product.images?.[0]}
          variantId={isOutfit ? outfitVariantId : firstSizeVariantId}
          sizes={allSizes}
        />

        {/* Variant overlay — outfit modda gösterilmez */}
        {!isOutfit && (
          <>
            {isVariantsOpen && hasVariants && (
              <div
                className={cn(
                  "absolute top-0 left-0 right-0 pointer-events-none hidden md:block animate-card-fade-in",
                  imageAspectClass,
                )}
              >
                <ProductCardVariants
                  variants={allVariants}
                  onClose={() => setIsVariantsOpen(false)}
                  onVariantHover={(image) => setHoveredVariantImage(image)}
                  onNavigate={onNavigate}
                />
              </div>
            )}

            {hasVariants && (
              <ProductCardVariantsSheet
                variants={allVariants}
                productName={
                  product.internet_product_description || product.name
                }
                isOpen={isSheetOpen}
                onClose={() => setIsSheetOpen(false)}
                onNavigate={onNavigate}
              />
            )}
          </>
        )}

        {!isOutfit && (
          <ProductCardMobileCartSheet
            product={product}
            isOpen={isMobileCartOpen}
            onClose={() => setIsMobileCartOpen(false)}
          />
        )}
      </article>
    );
  },
);

ProductCard.displayName = "ProductCard";
export default ProductCard;
